import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { joinList, joinErrorText, looksLikeEmail, PROMISE, FINE_PRINT_LEAD, JOINED } from '../utils/emailList';
import Icon from './Icon';
import './EmailSignup.css';

/**
 * The email form (utils/emailList.js): a labelled field, one button, then
 * the fine print. Used by the email gate and the questions-left line
 * (ChatView) and by "Stay in the loop" in the menu (below).
 *
 *   source     'gate' | 'card' | 'sidebar', where it was given
 *   promise    show the promise line above the field (the gate says it in
 *              its own words)
 *   secondary  a quieter action beside the button ("Sign in instead")
 *   children   a line between the buttons and the fine print
 *
 * Mistakes show inline, in words, and the address stays in the box.
 */
function EmailSignup({ source, submitLabel = 'Keep me posted', promise = true, autoFocus = false, onJoined, secondary = null, children = null }) {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);
  const id = useId();

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus({ preventScroll: true });
  }, [autoFocus]);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    const value = email.trim();
    if (!looksLikeEmail(value)) {
      setError('That doesn’t look like an email address. Please check it and try again.');
      if (inputRef.current) inputRef.current.focus();
      return;
    }
    setSending(true);
    setError('');
    try {
      await joinList(value, source);
      setSending(false);
      if (onJoined) onJoined();
    } catch (err) {
      setSending(false);
      setError(joinErrorText(err));
    }
  };

  return (
    <form className="email-signup" onSubmit={submit} noValidate>
      {promise && <p className="email-signup-promise">{PROMISE}</p>}
      <label className="email-signup-label" htmlFor={`${id}-email`}>Email</label>
      <input
        ref={inputRef}
        id={`${id}-email`}
        className="email-signup-input"
        type="email"
        inputMode="email"
        autoComplete="email"
        autoCapitalize="none"
        spellCheck={false}
        maxLength={254}
        placeholder="you@example.com"
        value={email}
        onChange={(e) => { setEmail(e.target.value); if (error) setError(''); }}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      {error && (
        <p className="apr-error email-signup-error" id={`${id}-error`} role="alert">
          <Icon name="triangle-alert" size={16} />
          <span>{error}</span>
        </p>
      )}
      <div className="email-signup-actions">
        <button type="submit" className="apr-btn" disabled={sending}>{sending ? 'Adding…' : submitLabel}</button>
        {secondary}
      </div>
      {children}
      <p className="email-signup-fine">
        {FINE_PRINT_LEAD} <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
      </p>
    </form>
  );
}

/**
 * "Stay in the loop" from the menu: the same form in a dialog (a sheet on
 * phones). Escape, the backdrop and the close button all close it. Once
 * this browser has given an address it says so instead of asking again.
 */
export function StayInTheLoopDialog({ onList, onClose }) {
  const [joined, setJoined] = useState(false);
  const closeRef = useRef(null);
  const done = joined || onList;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (done && closeRef.current) closeRef.current.focus({ preventScroll: true });
  }, [done]);

  return createPortal(
    <div className="apr-overlay" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="apr-dialog email-dialog" role="dialog" aria-modal="true" aria-labelledby="email-dialog-title">
        <span className="apr-dialog-grabber" aria-hidden="true" />
        <button type="button" className="apr-icon-btn apr-icon-btn--bare apr-dialog-close" onClick={onClose} aria-label="Close">
          <Icon name="x" size={20} />
        </button>
        <h2 id="email-dialog-title" className="apr-dialog-title">Stay in the loop</h2>
        {done ? (
          <>
            <p className="apr-notice email-dialog-done" role="status">
              <Icon name="check" size={20} />
              <span>{joined ? JOINED : 'You’re on the list.'} {PROMISE}</span>
            </p>
            <div className="apr-dialog-actions">
              <button ref={closeRef} type="button" className="apr-btn apr-btn--quiet" onClick={onClose}>Close</button>
            </div>
          </>
        ) : (
          <EmailSignup source="sidebar" autoFocus onJoined={() => setJoined(true)} />
        )}
      </div>
    </div>,
    document.body,
  );
}

export default EmailSignup;
